// Points scoring engine. RULES_SCHEMA.md §4.1. Deterministic and pure.

import type { PositionId, ScoringConfigLike, SlotId, StatKey, StatLine } from '@fsp/sports-core';

export interface GameLine {
  gameId: string;
  line: StatLine;
}

export interface ScoreBreakdownItem {
  stat: StatKey;
  ruleKind: 'unit' | 'bonus' | 'tier';
  /** The stat value that produced this item. */
  value: number;
  /** Contribution, before final rounding. */
  points: number;
  gameId?: string;
}

export interface PlayerScore {
  total: number;
  breakdown: readonly ScoreBreakdownItem[];
}

interface Positioned {
  stat: StatKey;
  positions: readonly PositionId[] | null;
}

function matches(rule: Positioned, positions: readonly PositionId[]): boolean {
  return rule.positions === null || rule.positions.some((p) => positions.includes(p));
}

/**
 * Group rules by a key; within a group, a position-specific rule that matches
 * beats a general one. Among several position-specific matches, first wins
 * (the validator warns about that case).
 */
function resolve<T extends Positioned>(
  rules: readonly T[],
  positions: readonly PositionId[],
  keyOf: (r: T) => string,
): T[] {
  const groups = new Map<string, T[]>();
  for (const r of rules) {
    if (!matches(r, positions)) continue;
    const k = keyOf(r);
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const chosen: T[] = [];
  for (const group of groups.values()) {
    const specific = group.find((r) => r.positions !== null);
    chosen.push(specific ?? group[0]!);
  }
  return chosen;
}

function sumStat(games: readonly GameLine[], stat: StatKey): number {
  let n = 0;
  for (const g of games) n += g.line[stat] ?? 0;
  return n;
}

function applyRounding(v: number, mode: 'none' | 'floor' | 'round'): number {
  if (mode === 'floor') return Math.floor(v);
  if (mode === 'round') return Math.round(v);
  return v;
}

export function roundTo(v: number, decimals: number): number {
  const f = 10 ** decimals;
  // Round half away from zero, so -0.5 → -1 and 0.5 → 1, matching how
  // people read a scoreboard.
  return (Math.sign(v) * Math.round(Math.abs(v) * f + 1e-9)) / f;
}

export function scorePlayer(
  games: readonly GameLine[],
  positions: readonly PositionId[],
  config: ScoringConfigLike,
): PlayerScore {
  const breakdown: ScoreBreakdownItem[] = [];

  for (const u of resolve(config.units, positions, (r) => r.stat)) {
    if (u.per === 1 || u.rounding === 'none') {
      const value = sumStat(games, u.stat);
      if (value !== 0)
        breakdown.push({
          stat: u.stat,
          ruleKind: 'unit',
          value,
          points: (value / u.per) * u.points,
        });
    } else {
      for (const g of games) {
        const value = g.line[u.stat] ?? 0;
        if (value === 0) continue;
        const units = applyRounding(value / u.per, u.rounding);
        breakdown.push({
          stat: u.stat,
          ruleKind: 'unit',
          value,
          points: units * u.points,
          gameId: g.gameId,
        });
      }
    }
  }

  for (const b of resolve(
    config.bonuses,
    positions,
    (r) => `${r.stat}@${r.threshold}@${r.scope}`,
  )) {
    if (b.scope === 'period') {
      const value = sumStat(games, b.stat);
      if (value >= b.threshold)
        breakdown.push({ stat: b.stat, ruleKind: 'bonus', value, points: b.points });
    } else {
      for (const g of games) {
        const value = g.line[b.stat] ?? 0;
        if (value >= b.threshold)
          breakdown.push({
            stat: b.stat,
            ruleKind: 'bonus',
            value,
            points: b.points,
            gameId: g.gameId,
          });
      }
    }
  }

  for (const t of resolve(config.tiers, positions, (r) => `${r.stat}@${r.scope}`)) {
    const bandPoints = (value: number): number | null => {
      for (const band of t.bands) {
        if (value >= band.min && (band.max === null || value <= band.max)) return band.points;
      }
      return null;
    };
    if (t.scope === 'period') {
      const value = sumStat(games, t.stat);
      const pts = bandPoints(value);
      if (pts !== null) breakdown.push({ stat: t.stat, ruleKind: 'tier', value, points: pts });
    } else {
      for (const g of games) {
        if (!(t.stat in g.line)) continue; // no band for a stat the game didn't report
        const value = g.line[t.stat] ?? 0;
        const pts = bandPoints(value);
        if (pts !== null)
          breakdown.push({ stat: t.stat, ruleKind: 'tier', value, points: pts, gameId: g.gameId });
      }
    }
  }

  const raw = breakdown.reduce((n, item) => n + item.points, 0);
  return { total: roundTo(raw, config.decimals), breakdown };
}

/**
 * A PROJECTED line's points: `scorePlayer`, with every tiered stat rounded to
 * a whole number before its band is looked up.
 *
 * A projection carries means, and a tier's bands are written for the whole
 * numbers a real game produces: 14–20 and 21–27 points allowed leave nothing
 * between 20 and 21 because no game ends there. A projected 20.4 fell in that
 * gap, matched no band and scored the tier as nothing — the same as 21 or
 * more under the default preset, and less than 13 or 35 under a league whose
 * bands are shaped otherwise (the review of round 2, NUMBERS-6). Rounded, it
 * scores the band a game with that many would, and a league's own gap stays
 * the gap it wrote: 3.2 in a league that pays nothing for 1–6 rounds to 3 and
 * is paid nothing. Only tiers are rounded; a unit is linear and a bonus is a
 * threshold, and a mean is already the right thing to hand either.
 *
 * Never used on a real line — a stat a game reported is scored exactly as
 * reported, whatever it is.
 */
export function scoreProjection(
  line: StatLine,
  positions: readonly PositionId[],
  config: ScoringConfigLike,
  gameId = 'projection',
): PlayerScore {
  const tiered = new Set(config.tiers.map((t) => t.stat));
  const whole: Record<StatKey, number> = {};
  for (const [stat, value] of Object.entries(line)) {
    whole[stat] = tiered.has(stat) ? Math.round(value) : value;
  }
  return scorePlayer([{ gameId, line: whole }], positions, config);
}

export interface LineupStarter {
  playerId: string;
  slot: SlotId;
  positions: readonly PositionId[];
  games: readonly GameLine[];
}

export function scoreLineup(
  starters: readonly LineupStarter[],
  config: ScoringConfigLike,
): { total: number; players: Readonly<Record<string, PlayerScore>> } {
  const players: Record<string, PlayerScore> = {};
  let total = 0;
  for (const s of starters) {
    const score = scorePlayer(s.games, s.positions, config);
    players[s.playerId] = score;
    total += score.total;
  }
  return { total: roundTo(total, config.decimals), players };
}

/** One stat's part in a player's points, folded: the shape `ScoreItem` in `@fsp/domain` is. */
export interface FoldedItem {
  stat: string;
  /** The stat's name, and "bonus" for a bonus: "Passing Yards", "Passing Yards bonus". */
  label: string;
  value: number;
  points: number;
}

/**
 * A week's points, stat by stat (B-218): `scorePlayer`'s items folded by stat
 * and kind, so two games in a week read as one line per stat and a bonus as
 * its own. Items worth nothing are left out; the order is the scorer's. The
 * game log's breakdown, a lineup row's and the simulator's are all this one
 * fold, so a score cannot explain itself two ways.
 */
export function foldBreakdown(
  items: readonly {
    stat: string;
    ruleKind: string;
    value: number;
    points: number;
    gameId?: string;
  }[],
  statName: (key: string) => string,
): FoldedItem[] {
  const out = new Map<string, FoldedItem & { games: Set<string> }>();
  for (const item of items) {
    const key = `${item.stat}|${item.ruleKind}`;
    const was = out.get(key) ?? {
      stat: item.stat,
      label: item.ruleKind === 'bonus' ? `${statName(item.stat)} bonus` : statName(item.stat),
      value: 0,
      points: 0,
      games: new Set<string>(),
    };
    // THE VALUE ONCE A GAME, the points every time: a 410-yard game crosses
    // the 300 and the 400 bonus, two items that each carry the 410, and the
    // yards read 820 (the review of 2026-09-27). Two games in a week do add.
    const game = item.gameId ?? '';
    if (!was.games.has(game)) {
      was.games.add(game);
      was.value += item.value;
    }
    was.points += item.points;
    out.set(key, was);
  }
  return [...out.values()]
    .map(({ games: _games, ...i }) => ({
      ...i,
      value: roundTo(i.value, 2),
      points: roundTo(i.points, 2),
    }))
    .filter((i) => i.points !== 0);
}
